// ---------------------------------------------------------------------------
// Tab 3 — Documents (file list + drag-drop upload)
// ---------------------------------------------------------------------------

import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { api } from '@/lib/api-client';
import { LoadingSpinner } from '@/components/common';
import type { PaymentDocument } from './types';

interface Props {
  documents: PaymentDocument[];
  onUpload: (file: File) => void;
  isUploading: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

async function handleDownloadClick(documentId: number) {
  const { url } = await api.get<{ url: string }>(`/api/documents/${documentId}/download`);
  window.location.href = url;
}

export function DocumentsTab({ documents, onUpload, isUploading }: Props) {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onUpload(acceptedFiles[0]);
      }
    },
    [onUpload],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: isUploading,
  });

  return (
    <div className="space-y-4">
      {/* Existing documents */}
      {documents.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
            Attached Files
          </h4>
          <div className="divide-y divide-[#f0f0f0] rounded-lg border border-[#e0e0e0]">
            {documents.map((doc) => (
              <div key={doc.document_id} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-[#333]">{doc.file_name}</p>
                  <p className="text-[11px] text-[#999]">
                    {formatFileSize(doc.file_size)} &middot; {formatDate(doc.upload_date)} &middot;{' '}
                    {doc.uploaded_by}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDownloadClick(doc.document_id)}
                  className="ml-3 text-xs font-medium text-[#6B0F1A] hover:underline"
                >
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-[13px] text-[#999]">No documents attached yet.</p>
      )}

      {/* Upload zone */}
      <div>
        <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
          Upload Document
        </h4>
        <div
          {...getRootProps()}
          aria-disabled={isUploading}
          className={`rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors ${
            isUploading
              ? 'cursor-not-allowed border-[#ddd] bg-[#f5f5f5] opacity-60'
              : isDragActive
                ? 'cursor-pointer border-[#6B0F1A] bg-[#fdf0f2]'
                : 'cursor-pointer border-[#ddd] bg-[#fafafa] hover:border-[#bbb]'
          }`}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <LoadingSpinner size="sm" text="Uploading..." />
          ) : isDragActive ? (
            <p className="text-sm font-medium text-[#6B0F1A]">Drop file here</p>
          ) : (
            <p className="text-sm text-[#888]">Drag a file here or click to browse</p>
          )}
        </div>
      </div>
    </div>
  );
}
